import { useTranslation } from 'react-i18next'
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'
import { cn } from '@/lib/utils'

const productLinks = [
  { key: 'nav.productCategories.mortadelle', to: 'products' },
  { key: 'nav.productCategories.prosciuttiCrudi', to: 'products' },
  { key: 'nav.productCategories.prosciuttiCotti', to: 'products' },
  { key: 'nav.productCategories.salami', to: 'products' },
  { key: 'nav.productCategories.arrosti', to: 'products' },
]

const companyLinks = [
  { key: 'nav.about', to: 'about' },
  { key: 'nav.recipes', to: 'recipes' },
  { key: 'nav.news', to: 'news' },
  { key: 'nav.nutrition', to: 'nutrition' },
  { key: 'nav.workWithUs', to: 'workWithUs' },
]

const legalLinks = [
  { key: 'footer.privacy', to: 'privacy' },
  { key: 'footer.cookiePolicy', to: 'privacy' },
  { key: 'footer.legalNotes', to: 'privacy' },
]

const socialLinks = [
  { icon: Facebook, href: 'https://facebook.com/fioruccisalumi', label: 'Facebook' },
  { icon: Instagram, href: 'https://instagram.com/fioruccisalumi', label: 'Instagram' },
  { icon: Youtube, href: 'https://youtube.com/fioruccisalumi', label: 'YouTube' },
  { icon: Linkedin, href: 'https://linkedin.com/company/fiorucci', label: 'LinkedIn' },
]

export function Footer() {
  const { t } = useTranslation('common')
  const currentYear = new Date().getFullYear()

  return (
    <footer className="relative bg-[#2A0E0E] text-white overflow-hidden" role="contentinfo">
      {/* Subtle grain texture overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Top accent line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#DF631E]/60 to-transparent" />

      {/* Main content */}
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-16 pb-12">
        {/* Upper section: brand + columns */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">

          {/* Brand block — large wordmark + tagline */}
          <div className="lg:col-span-5 space-y-6">
            <LocalizedLink to="home" className="inline-block group">
              <img
                src="/images/fiorucci-logo.svg"
                alt="Fiorucci Salumi"
                className="h-14 md:h-16 w-auto"
              />
            </LocalizedLink>

            <p
              className="text-white/50 text-sm leading-relaxed max-w-sm tracking-wide"
              style={{ fontFamily: 'Playfair Display, Georgia, serif', fontStyle: 'italic' }}
            >
              {t('footer.tagline')}
            </p>

            {/* Social row */}
            <div className="flex items-center gap-3 pt-2">
              {socialLinks.map((social) => {
                const Icon = social.icon
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className={cn(
                      'w-9 h-9 rounded-full border border-white/15 flex items-center justify-center',
                      'text-white/50 transition-all duration-300',
                      'hover:border-[#DF631E] hover:text-[#DF631E] hover:scale-110'
                    )}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                )
              })}
            </div>
          </div>

          {/* Nav columns */}
          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-8">
            {/* Products */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-5">
                {t('nav.products')}
              </h3>
              <ul className="space-y-3">
                {productLinks.map((link) => (
                  <li key={link.key}>
                    <LocalizedLink
                      to={link.to}
                      className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {t(link.key)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-5">
                {t('footer.navigation')}
              </h3>
              <ul className="space-y-3">
                {companyLinks.map((link) => (
                  <li key={link.key}>
                    <LocalizedLink
                      to={link.to}
                      className="text-sm text-white/60 hover:text-white transition-colors duration-200"
                    >
                      {t(link.key)}
                    </LocalizedLink>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-[0.2em] text-white/40 mb-5">
                {t('footer.contacts')}
              </h3>
              <ul className="space-y-3">
                <li>
                  <a
                    href="tel:+3906911931"
                    className="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-2"
                  >
                    <Phone className="w-3.5 h-3.5 text-[#DF631E]/70" />
                    +39 06911931
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@fioruccifood.it"
                    className="text-sm text-white/60 hover:text-white transition-colors duration-200 flex items-center gap-2"
                  >
                    <Mail className="w-3.5 h-3.5 text-[#DF631E]/70" />
                    info@fioruccifood.it
                  </a>
                </li>
                <li className="flex items-start gap-2 text-sm text-white/50 leading-relaxed">
                  <MapPin className="w-3.5 h-3.5 text-[#DF631E]/70 flex-shrink-0 mt-0.5" />
                  <span>
                    Cesare Fiorucci S.p.A.<br />
                    Via del Commercio, 12<br />
                    00040 Pomezia (RM)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="mt-14 mb-6 h-px bg-white/8" />

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/35 tracking-wide">
            &copy; {currentYear} Cesare Fiorucci S.p.A. &mdash; {t('footer.allRightsReserved')}
          </p>

          <nav aria-label={t('footer.legal')} className="flex flex-wrap items-center gap-5">
            {legalLinks.map((link) => (
              <LocalizedLink
                key={link.key}
                to={link.to}
                className="text-xs text-white/35 hover:text-white/70 transition-colors duration-200 tracking-wide"
              >
                {t(link.key)}
              </LocalizedLink>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  )
}
